import { getMarkets } from "@dotcom/markets";
import { getPageMeta, getAlternateLinks, mergeMeta } from "@dotcom/remix-meta";
import styled from "@emotion/styled";
import {
  MediaQueries,
  Spacer,
  Spacings,
} from "@polestar/component-warehouse-react";
import { json, type LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";

import { Filter, NewsArticles, Pagination } from "./components";
import { getNewsOverview } from "./data";
import { getNewsTagId, getCurrentPage } from "./helpers";

import { Section } from "~/common/components";
import {
  getNewsArticles,
  getErrorPageData,
  getNewsTags,
  getNewsArticleCount,
} from "~/common/data";
import { getIsChina } from "~/common/helpers/.server";
import { type RootLoader } from "~/root";

const StyledSectionWrapper = styled(Section)`
  margin-top: 0;

  ${MediaQueries.desktop} {
    margin-top: 0;
  }
`;

export const meta = mergeMeta<typeof loader, { root: RootLoader }>(
  ({ data, location }) => {
    if (!data) return [];

    const { seo, hasNoRobotMeta } = data.newsOverviewData;

    const { markets, isChina } = data;

    return [
      ...getPageMeta({
        title: seo?.title,
        description: seo?.description,
        image: seo?.image?.url,
        twitterCard: seo?.twitterCard,
        noIndex: hasNoRobotMeta,
      }),
      ...getAlternateLinks(location.pathname, markets, isChina),
    ];
  }
);

export const loader = async ({
  request,
  context: { market, datoClient },
}: LoaderFunctionArgs) => {
  if (!market) {
    throw json(
      await getErrorPageData(datoClient, { requestUrl: request.url }),
      { status: 404 }
    );
  }
  const isChina = getIsChina();
  const markets = getMarkets(datoClient.isPreview, isChina);

  const newsTagId = getNewsTagId(request.url);
  const currentPage = getCurrentPage(request.url);

  const first = 15;
  const index = currentPage - 1;
  const skip = index * first;

  const [newsOverviewData, newsTags, articleCount, newsArticles, dictionary] =
    await Promise.all([
      getNewsOverview(datoClient),
      getNewsTags(datoClient),
      getNewsArticleCount(datoClient, { newsTagId }),
      getNewsArticles(datoClient, {
        skip,
        newsTagId,
        market,
      }),
      datoClient.getFromDictionary([
        "Read more",
        "Highlighted",
        "Previous",
        "Next",
        "All",
        "Subject",
        "ARIA Go to page",
      ]),
    ]);

  const pageCount = Math.ceil(articleCount / first);

  return {
    isChina,
    market,
    markets,
    newsTagId,
    currentPage,
    newsOverviewData,
    newsTags,
    pageCount,
    newsArticles,
    dictionary,
  };
};

const NewsOverviewPageLayout = () => {
  const {
    newsOverviewData,
    newsTagId,
    newsTags,
    currentPage,
    pageCount,
    newsArticles,
    dictionary,
    market,
  } = useLoaderData<typeof loader>();

  return (
    <StyledSectionWrapper>
      <Spacer spacing={{ mobile: Spacings.xLarge, tablet: Spacings.xxLarge }} />

      <Filter
        title={newsOverviewData.title}
        newsTagId={newsTagId}
        newsTags={newsTags}
        dropdownLabel={dictionary.Subject ?? "Subject"}
        defaultOptionLabel={dictionary.All ?? "All"}
      />

      <NewsArticles
        locale={market.locale}
        newsArticles={newsArticles.articles}
        highlightedLabel={dictionary.Highlighted ?? "Highlighted"}
        readMoreLabel={dictionary["Read more"] ?? "Read more"}
      />

      <Pagination
        currentPage={currentPage}
        pageCount={pageCount}
        newsTagId={newsTagId}
        nextButtonAriaLabel={dictionary.Next ?? "Next"}
        previousButtonAriaLabel={dictionary.Previous ?? "Previous"}
        goToPageAriaLabel={dictionary["ARIA Go to page"] ?? "Go to page"}
      />
    </StyledSectionWrapper>
  );
};

export default NewsOverviewPageLayout;
